import React, {
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback,
  useMemo,
  SetStateAction,
  Dispatch,
} from "react";
import { useHistory } from "react-router-dom";
import { renderToStaticMarkup } from "react-dom/server";

import { baseXml, decodeXmlUrl, encodeXmlUrl } from "../../utils/panoXmlUtils";
import {
  addHotspot,
  addQuestionHotspot,
  addSurveyHotspot,
  addTextHotspot,
  getCoordinates,
  getSlugWithoutIndexSuffix,
  krpanoId,
  removeQuestionHotspot,
  removeHotspot,
  removeVrPoiHotspot,
  renderVrPoiHotspot,
  HardPanoViewLimits,
} from "../../utils/panoUtils";
import { Hotspot, Pano, PanoScenario, PanoView, UseStateReturn } from "../../types";
import { useRouteState } from "../../hooks/useRouteState";
import { ReactComponent as LoaderIcon } from "./../../images/loader.svg";
import { ReactComponent as PlayIcon } from "./../../images/play.svg";
import questionMarker from "./../../images/markers/pinUser.svg";
import debounce from "lodash.debounce";
import { PanoMedia, Loader, PlayButton } from "./PanoStyles";
import { makeNativeKeyDownHandler } from "../../utils/a11y-utils";
import { useMediaQuery } from "react-responsive";
import { breakpoints } from "../../utils/styleUtils";
import classNames from "classnames";
import { useInterfaceState } from "../../InterfaceContext";
import { useContentUrl } from "../../hooks/useContentUrl";
import { useAppState } from "../../AppContext";
import { useSessionStorage } from "../../hooks/useStorage";
import { usePanoUrl } from "../../hooks/usePanoUrl";
import { useIntl } from "react-intl";
import {
  findAnswer,
  fromPanoSourceImageUrl,
  isLocationQuestion,
  isPanoramaSource,
  isPanoramaView,
  LocalPinModel,
  PlacePanoPinOptions,
  toPanoSourceImageUrl,
  useSurveyState,
} from "../../SurveyContext";
import {
  CreateLocationPickerAnswerModel,
  PanoLocationPinModel,
  PinModel,
} from "../../tim-survey/EndUserApi";
import { useCopy } from "../../hooks/useCopy";
import { isPanoTourPoint } from "../../utils/tourUtils";
import { OffscreenPanoMarker } from "./OffscreenPanoMarker";
import { Krpano as KrpanoNS, KrpanoElement } from "../../libs/krpano";

require("../../libs/krpano.js");
declare const embedpano: (params: KrpanoNS.EmbedParameters) => void;
declare const removepano: (id: string) => void;

export const useKrpano = () => document.querySelector<KrpanoElement>(`#${krpanoId}`);

export type View = {
  vlookat: number;
  hlookat: number;
  fov: number;
};

export type EditorVertexHotspot = KrpanoNS.Hotspot & {
  relatedHotspot: KrpanoNS.Hotspot;
  vertexIndex: number;
};

type KrpanoProps = {
  showFade: boolean;
  fade: UseStateReturn<boolean>;
  xml: string;
  onViewChange?: (view: PanoView) => void;
  pano?: Pano;
  view: PanoView;
  scenario?: PanoScenario;
  children: (krpano: KrpanoElement) => void;
  hotspots?: Hotspot[];
  vrHotspots?: Hotspot[];
  isVideo?: boolean;
  returnAtEndOfVideo?: boolean;
  showVideoControls?: boolean;
  teasing: boolean;
  setTeasing: Dispatch<SetStateAction<boolean>>;
  setSwitchScenario: Dispatch<SetStateAction<string | null>>;
  setGyroActive: Dispatch<SetStateAction<boolean>>;
};

const hlookatConverter = (value: number): number => {
  value = value % 360;
  if (value < 0) value = value + 360;
  return value;
};

/** Some krpano values are slightly, slightly different from the previous values */
const significantDifference = (a: number, b: number): boolean => Math.abs(a - b) > 0.01;

const krpanoContainerId = "pano";
const KrpanoViewer = React.memo(() => <div id={krpanoContainerId} />);

const Krpano = ({
  showFade,
  fade,
  xml,
  onViewChange,
  pano,
  scenario,
  hotspots = [],
  vrHotspots = [],
  view,
  children,
  returnAtEndOfVideo = false,
  showVideoControls = true,
  isVideo = false,
  teasing,
  setTeasing,
  setSwitchScenario,
  setGyroActive,
}: KrpanoProps) => {
  const [faded, setFaded] = fade;
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [krpanoContainer, setKrpanoContainer] = useState(() =>
    document.getElementById(krpanoContainerId)
  );
  const {
    setInterfaceState,
    interfaceState,
    setParticipationState,
    participationState,
    setIsInVr,
    isInVr,
  } = useInterfaceState();
  const { pinPlaceFn, surveyState, setSurveyState, openModalFn } = useSurveyState();
  const history = useHistory();
  const prevSlug = useRef<string>("");
  const hasPreviousPano = useRef<boolean>(false);
  const prevScenarioSlug = useRef<string>("");
  const prevPoiSlug = useRef<string | null>(null);
  // Return to pano after video play
  const prevPanoView = useRef<any>(null);
  const routeState = useRouteState(true);
  const isBigScreen = useMediaQuery({ query: breakpoints[30] });
  const contentUrl = useContentUrl();
  const { state } = useAppState();
  const [disablePanoScenarioTeaser] = useSessionStorage("disablePanoScenarioTeaser");
  const [loadedPano, setLoadedPano] = useSessionStorage("loadedPano");
  const [panoDragged, setPanoDragged] = useSessionStorage("panoDragged");
  const panoUrl = usePanoUrl();
  const intl = useIntl();
  const copy = useCopy();
  const lastPointerEventRef = useRef<PointerEvent>();
  const limits = useMemo(() => pano?.limits || HardPanoViewLimits, [pano?.limits]);
  const krpano = useKrpano();

  const { tourSlug, tourPointSlug } = routeState;

  const tour = useMemo(
    () => state.tours.find((tour) => tour.slug === tourSlug) ?? null,
    [state.tours, tourSlug]
  );

  const tourPoint = useMemo(
    () => tour?.points.find((point) => point.slug === tourPointSlug),
    [tourPointSlug, tour?.points]
  );

  const panoramaView = useMemo(
    () =>
      surveyState.activePage?.initialView && isPanoramaView(surveyState.activePage.initialView)
        ? surveyState.activePage.initialView
        : null,
    [surveyState.activePage?.initialView]
  );

  const panoNameForSurvey = useMemo(
    () => panoramaView?.dataSource.name || pano!.slug,
    [panoramaView, pano]
  );


  const teaseScenario = useMemo(
    () => pano?.scenarios.find((scenario) => scenario.id === state.teaseScenario?.id),
    [state.teaseScenario, pano]
  );

  const vrPoiContent = useMemo(() => {
    if (isInVr && routeState.contentSlug && interfaceState.vr.pois[routeState.contentSlug]) {
      return interfaceState.vr.pois[routeState.contentSlug];
    }
  }, [isInVr, routeState, interfaceState.vr.pois]);

  const activeVrHotspot = useMemo(() => {
    if (isInVr && routeState.contentSlug) {
      return vrHotspots.find((h) => getSlugWithoutIndexSuffix(h) === routeState.contentSlug);
    }
  }, [isInVr, routeState, vrHotspots]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setKrpanoContainer(document.getElementById(krpanoContainerId));
  });

  useEffect(() => {
    if (!krpano) return;

    if (vrPoiContent && activeVrHotspot) {
      renderVrPoiHotspot(krpano, vrPoiContent, activeVrHotspot).then(() => {
        const closeButton = krpano.get("hotspot[vr-poi-close]")!;
        closeButton.onclick = () => {
          history.push(contentUrl());
        };
      });
    } else {
      removeVrPoiHotspot(krpano);
    }
  }, [vrPoiContent, activeVrHotspot, krpano, contentUrl, history]);

  const resetIdle = useCallback(() => {
    if (teaseScenario) krpano?.call("autorotate.interrupt");
    setTeasing(false);
  }, [teaseScenario, setTeasing, krpano]);

  useEffect(() => {
    if (init && krpano) {
      if (window.self === window.top) {
        krpano.focus();
      }
      resetIdle();
    }
  }, [resetIdle, init, krpano]);

  const onPanoHotspotClick = useCallback(
    (hs: any) => {
      if (!krpano) return;
      krpano?.set("is_hs_click", true);
      const { vlookat, hlookat, fov } = krpano.get("view");
      prevPanoView.current = { vlookat, hlookat, fov };
      setParticipationState((state) => ({
        ...state,
        navigatedPano: true,
      }));
      history.push(decodeXmlUrl(hs.linkurl));
    },
    [history, setParticipationState, krpano]
  );

  const tease = useCallback(async () => {
    if (!krpano || !pano || !scenario) return;

    const disabled = Boolean(!krpano.get("idletime") || disablePanoScenarioTeaser !== null);
    if (!disabled && teaseScenario && !teasing && teaseScenario !== scenario && !isInVr) {
      setTeasing(true);

      try {
        await new Promise<void>((resolve, reject) => {
          krpano.set("events[teaser].onblendcomplete", resolve);
          krpano.set("events[teaser].onmousedown", reject);
          krpano.set("events[teaser].keep", true);
          krpano.call(
            `loadxml(${xml.replace(
              scenario.resource.join("|"),
              teaseScenario.resource.join("|")
            )}, null, MERGE, SLIDEBLEND(1.5, -90.0, 0.2, easeinoutquad));`
          );
        });
      } catch (error) {
        // immediately cancel transition
        krpano.call(`
          delete(events[teaser]);
          loadxml(${xml}, null, MERGE, NOBLEND)`);
        return resetIdle();
      }

      await new Promise<void>((resolve, reject) => {
        krpano.set("events[teaser].onblendcomplete", resolve);
        krpano.set("events[teaser].onmousedown", reject);
        krpano.call(`loadxml(${xml}, null, MERGE, SLIDEBLEND(1.5, 90.0, 0.2, easeinoutquad));`);
      }).catch(() => {
        // immediately cancel transition
        krpano?.call(`loadxml(${xml}, null, MERGE, NOBLEND)`);
      });

      // remove transition events
      krpano.call("delete(events[teaser])");

      resetIdle();
    }
  }, [
    teaseScenario,
    resetIdle,
    scenario,
    pano,
    xml,
    teasing,
    setTeasing,
    disablePanoScenarioTeaser,
    isInVr,
    krpano,
  ]);

  useEffect(() => {
    krpano?.set("events.onidle", teaseScenario ? tease : null);
  }, [tease, teaseScenario, krpano]);

  useEffect(() => {
    if (!krpano) return;

    if (interfaceState.question?.location) {
      addQuestionHotspot(krpano, {
        coordinates: [
          {
            yaw: interfaceState.question.location.longitude,
            pitch: interfaceState.question.location.latitude,
          },
        ],
        icon: questionMarker,
        title: "question",
        slug: "question",
        url: "question",
        showBadge: false,
        allowHighlight: false,
      });
    } else {
      removeQuestionHotspot(krpano);
    }
  }, [interfaceState.question?.location, krpano]);

  const embedKrpano = useCallback(
    async (container: HTMLElement) => {
      if (document.hidden) {
        await new Promise<void>((resolve) => {
          const listener = () => {
            document.removeEventListener("visibilitychange", listener);
            resolve();
          };
          document.addEventListener("visibilitychange", listener);
        });
      }

      if (!document.getElementById(krpanoId)) {
        return new Promise<void>((resolve) => {
          embedpano({
            xml: null,
            consolelog: process.env.NODE_ENV === "development",
            id: krpanoId,
            target: container,
            onready: (krpanoInterface: KrpanoNS.Global) =>
              requestAnimationFrame(() => {
                const krpano = document.querySelector<KrpanoElement>(`#${krpanoId}`)!;
                const idletime = state.teaseIdletime;

                krpanoInterface.actions.loadxml(
                  baseXml({ idletime, view, limits }),
                  null,
                  null,
                  null,
                  () => resolve()
                );
                krpano.setAttribute("tabIndex", "0");
                krpano.setAttribute("data-html2canvas-ignore", "");

                krpanoInterface.display.customFullscreenElement = document.body;
              }),
          });
        });
      }
    },
    [view, state.teaseIdletime]
  );

  useEffect(() => {
    if (!krpanoContainer) return;

    let isCurrentRender = true;

    const timeout = window.setTimeout(async () => {
      if (isCurrentRender) {
        await embedKrpano(krpanoContainer);
        if (isCurrentRender) setInit(true);
      }
    }, 50);

    return () => {
      window.clearTimeout(timeout);
      isCurrentRender = false;
    };
  }, [krpanoContainer, embedKrpano]);

  useLayoutEffect(() => {
    if (krpanoContainer)
      return () => {
        // Keep state when hot-reloading
        if (!krpanoContainer.isConnected) {
          setInterfaceState((currentState) => ({ ...currentState, question: undefined }));
          removepano(krpanoId);
          setInit(false);
        }
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [krpanoContainer]);

  const hotspotExists = useCallback(
    (hotspotName: string) => {
      return Boolean(krpano?.get(`hotspot[${hotspotName}]`));
    },
    [krpano]
  );

  useEffect(() => {
    if (!krpano || !pano || !scenario) return;

    krpano
      .get("hotspot")
      .getArray()
      .filter((hs: any) => !hs.name.startsWith("vr_") && !hs.vr)
      .forEach((hs: any) => {
        if (hs.name.startsWith("editor-")) krpano.call(`removehotspot(${hs.name})`);
        else {
          hs.visible = true;
          hs.enabled = true;
        }
      });
  }, [pano, panoUrl, state.panos, onPanoHotspotClick, hotspotExists, scenario, intl, krpano]);

  useLayoutEffect(() => {
    if (!loading && !faded && krpano) {
      if (showFade) {
        setFaded(true);
        krpano.call(`intro(${view.hlookat}, ${view.vlookat}, ${view.fov})`);
        if (loadedPano === null) setLoadedPano("true");
      } else if (!panoDragged && krpano.get("autorotate")) {
        krpano.call("events.dispatch(onintrocomplete)");
      }
    }
  }, [loading, showFade, faded, setFaded, view, panoDragged, loadedPano, setLoadedPano, krpano]);

  const usedHotspots = useMemo(
    () => (isInVr ? vrHotspots : hotspots),
    [isInVr, vrHotspots, hotspots]
  );

  const movePanoIndicators = useCallback(() => {
    if (!krpano || !pano || pano.isIndoor || panoramaView) return;

    const panoSinglePointHotspots = usedHotspots
      .filter((hs) => !hs.phantom)
      .filter((hs) => ["high", "low"].includes(hs.type!))
      .map((hotspot) => ({
        krpanoHotspot: krpano.get(`hotspot[${hotspot.slug}]`)!,
        hotspot,
      }))
      .filter(({ krpanoHotspot }) => krpanoHotspot?.visible)
      .sort((a, b) => a.krpanoHotspot.atv - b.krpanoHotspot.atv);

    const view = krpano.get("view");

    let hlookat = view.hlookat % 360;
    if (hlookat < -180) hlookat += 360;
    if (hlookat > 180) hlookat -= 360;

    const screenMargin = 2;
    const ySpacing = 40;
    const moveLabelWithIndicator = isBigScreen;

    type HotspotPositionData = typeof panoSinglePointHotspots[number] & { x: number };

    if (isInVr) {
      return panoSinglePointHotspots.forEach(({ krpanoHotspot, hotspot }) => {
        if (!krpanoHotspot.flying) return; // skip if offscreen state hasn't changed

        krpanoHotspot.flying = 0;
        krpanoHotspot.url = "data:image/svg+xml;utf8," + encodeURIComponent(hotspot.icon!);
        krpanoHotspot.ox = 0;
        krpanoHotspot.oy = 0;
        krpanoHotspot.zorder = 51;
        krpanoHotspot.onclick = () => onPanoHotspotClick(krpanoHotspot);

        const label = krpano.get(`hotspot[${hotspot.slug}-text]`);
        if (label) {
          label.onclick = krpanoHotspot.onclick;
          label.flying = 0;
          label.oy = 0;
          label.ox = 25;
          label.edge = "left";
          label.alpha = 1;
          label.zorder = krpanoHotspot.zorder;
        }
      });
    }

    const positions = panoSinglePointHotspots
      .filter((hs) => hs.krpanoHotspot.loaded)
      .reduce(
        (
          acc: {
            isInView: typeof panoSinglePointHotspots[number][];
            isLeft: HotspotPositionData[];
            isRight: HotspotPositionData[];
          },
          hotspotData
        ) => {
          const { krpanoHotspot } = hotspotData;
          const loader = krpanoHotspot.loader as HTMLImageElement;
          const offscreenMarginX = (krpanoHotspot.width as number) / 2;
          const offscreenMarginY =
            Number(krpanoHotspot.height) / 2 ||
            (offscreenMarginX * loader.naturalHeight) / loader.naturalWidth;
          const screenPosition = krpano.spheretoscreen(krpanoHotspot.ath, krpanoHotspot.atv);

          const isInView =
            screenPosition.x >= -offscreenMarginX &&
            screenPosition.x <= krpano.clientWidth + offscreenMarginX &&
            screenPosition.y >= -offscreenMarginY &&
            screenPosition.y <= krpano.clientHeight + offscreenMarginY;

          let lookAtOffsetX = (krpanoHotspot.ath - hlookat) % 360;
          if (Math.abs(lookAtOffsetX) > 180) {
            lookAtOffsetX = lookAtOffsetX - 360 * Math.sign(lookAtOffsetX);
          }

          switch (true) {
            case isInView:
              acc.isInView.push(hotspotData);
              break;
            case !Number.isNaN(screenPosition.x) && screenPosition.x < krpano!.clientWidth / 2:
            case Number.isNaN(screenPosition.x) && Math.sign(lookAtOffsetX) === -1:
              acc.isLeft.push({ ...hotspotData, x: lookAtOffsetX });
              break;
            case !Number.isNaN(screenPosition.x) && screenPosition.x >= krpano!.clientWidth / 2:
            case Number.isNaN(screenPosition.x) && Math.sign(lookAtOffsetX) === 1:
              acc.isRight.push({ ...hotspotData, x: lookAtOffsetX });
              break;
            default:
              console.warn("Out of view hotspot could not be placed:", hotspotData.hotspot.slug);
          }
          return acc;
        },
        {
          isInView: [],
          isLeft: [],
          isRight: [],
        }
      );

    positions.isInView.forEach(({ krpanoHotspot, hotspot }) => {
      if (!krpanoHotspot.flying) return; // skip if offscreen state hasn't changed

      krpanoHotspot.flying = 0;
      krpanoHotspot.url = "data:image/svg+xml;utf8," + encodeURIComponent(hotspot.icon!);
      krpanoHotspot.ox = 0;
      krpanoHotspot.oy = 0;
      krpanoHotspot.zorder = 51;
      krpanoHotspot.onclick = () => onPanoHotspotClick(krpanoHotspot);

      const label = krpano.get(`hotspot[${hotspot.slug}-text]`);
      if (label) {
        label.onclick = krpanoHotspot.onclick;
        label.flying = 0;
        label.oy = 0;
        label.ox = 25;
        label.edge = "left";
        label.alpha = 1;
        label.zorder = krpanoHotspot.zorder;
      }
    });

    positions.isLeft
      .sort((a, b) => b.x - a.x || b.krpanoHotspot.atv - a.krpanoHotspot.atv)
      .forEach(({ krpanoHotspot, hotspot }, i) => {
        const offscreenMarginX = Number(krpanoHotspot.width) / 2;

        krpanoHotspot.flying = 1;

        krpanoHotspot.url =
          "data:image/svg+xml;utf8," +
          encodeURIComponent(
            renderToStaticMarkup(
              <OffscreenPanoMarker high={hotspot.type === "high"} rotation={-90} />
            )
          );

        krpanoHotspot.ox = -(krpano.clientWidth / 2 - (offscreenMarginX + screenMargin));
        krpanoHotspot.oy = (i - (positions.isLeft.length - 1) / 2) * ySpacing;

        krpanoHotspot.zorder = 40;
        krpanoHotspot.onclick = () => {
          krpano.call(`looktohotspot(${hotspot.slug}, get(view.fov), tween(easeoutquad, 1), true)`);
        };

        const label = krpano.get(`hotspot[${hotspot.slug}-text]`);
        if (label && moveLabelWithIndicator) {
          label.onclick = krpanoHotspot.onclick;
          label.flying = 1;
          label.oy = krpanoHotspot.oy;
          label.ox = krpanoHotspot.ox + 25;
          label.edge = "left";
          label.alpha = 0.75;
          label.zorder = krpanoHotspot.zorder;
        }
      });

    positions.isRight
      .sort((a, b) => a.x - b.x || a.krpanoHotspot.atv - b.krpanoHotspot.atv)
      .forEach(({ krpanoHotspot, hotspot }, i) => {
        const offscreenMarginX = Number(krpanoHotspot.width) / 2;

        krpanoHotspot.flying = 1;

        krpanoHotspot.url =
          "data:image/svg+xml;utf8," +
          encodeURIComponent(
            renderToStaticMarkup(
              <OffscreenPanoMarker high={hotspot.type === "high"} rotation={90} />
            )
          );

        krpanoHotspot.ox = krpano.clientWidth / 2 - (offscreenMarginX + screenMargin);
        krpanoHotspot.oy = (i - (positions.isRight.length - 1) / 2) * ySpacing;

        krpanoHotspot.zorder = 40;
        krpanoHotspot.onclick = () => {
          krpano.call(`looktohotspot(${hotspot.slug}, get(view.fov), tween(easeoutquad, 1), true)`);
        };

        const label = krpano.get(`hotspot[${hotspot.slug}-text]`);
        if (label && moveLabelWithIndicator) {
          label.onclick = krpanoHotspot.onclick;
          label.flying = 1;
          label.oy = krpanoHotspot.oy;
          label.ox = krpanoHotspot.ox - 25;
          label.edge = "right";
          label.alpha = 0.75;
          label.zorder = krpanoHotspot.zorder;
        }
      });
  }, [krpano, pano, usedHotspots, isBigScreen, isInVr, onPanoHotspotClick, panoramaView]);

  useEffect(() => {
    if (!krpano) return;

    krpano
      ?.get("hotspot")
      .getArray()
      .filter((hs) => usedHotspots.some((uhs) => uhs.slug === hs.name))
      .map((hs) => ({
        krpanoHotspot: hs,
        hotspot: usedHotspots.find((uhs) => uhs.slug === hs.name)!,
      }))
      .filter(({ krpanoHotspot, hotspot }) => {
        if (hotspot.coordinates.length === 1) {
          return (
            krpanoHotspot.ath !== hotspot.coordinates[0].yaw ||
            krpanoHotspot.atv !== hotspot.coordinates[0].pitch
          );
        } else {
          return krpanoHotspot.point
            .getArray()
            .some(
              (p, i) =>
                p.ath !== hotspot.coordinates[i].yaw && p.atv !== hotspot.coordinates[i].pitch
            );
        }
      })
      .forEach(({ krpanoHotspot, hotspot }) => {
        if (hotspot.coordinates.length === 1) {
          krpano.set(`hotspot[${krpanoHotspot.name}].ath`, hotspot.coordinates[0].yaw);
          krpano.set(`hotspot[${krpanoHotspot.name}].atv`, hotspot.coordinates[0].pitch);
          krpano.set(`hotspot[${krpanoHotspot.name}-text].ath`, hotspot.coordinates[0].yaw);
          krpano.set(`hotspot[${krpanoHotspot.name}-text].atv`, hotspot.coordinates[0].pitch);
        } else {
          hotspot.coordinates.forEach((c, i) => {
            krpano.set(`hotspot[${krpanoHotspot.name}].point[${i}].ath`, c.yaw);
            krpano.set(`hotspot[${krpanoHotspot.name}].point[${i}].atv`, c.pitch);
          });
        }
      });
  }, [usedHotspots, krpano]);

  const findPoi = useCallback(
    (slug: string) => usedHotspots.find((hs) => getSlugWithoutIndexSuffix(hs) === slug),
    [usedHotspots]
  );

  const closePoi = useCallback(() => history.replace(contentUrl()), [history, contentUrl]);

  const loadPoi = useCallback(
    (hotspot: Hotspot | undefined) => {
      if (hotspot && krpano) {
        if (!isBigScreen) return;

        if (hotspot.slug !== routeState.poiSlug || !krpano.get("hotspot[text-hotspot]")) {
          const textHotspot = addTextHotspot(krpano, hotspot, contentUrl(hotspot.slug));
          if (!textHotspot) return;

          textHotspot.onloaded = () => {
            if (!textHotspot.sprite.classList.contains("pano-poi")) {
              textHotspot.sprite.classList.add("pano-poi");
              const closeButton = textHotspot.sprite.appendChild(document.createElement("button"));
              closeButton.textContent = "";
              closeButton.setAttribute(
                "aria-label",
                intl.formatMessage({
                  id: "pano.poi-close.aria-label",
                  defaultMessage: "Close point of interest",
                  description: "ARIA label for a close-poi button",
                })
              );
              closeButton.classList.add("pano-poi-close");
              closeButton.addEventListener("click", closePoi);
              closeButton.addEventListener("keydown", makeNativeKeyDownHandler(closePoi));
            }
          };
          if (textHotspot.loaded) textHotspot.onloaded();
          prevPoiSlug.current = routeState.poiSlug || null;
        }
      } else {
        prevPoiSlug.current = routeState.poiSlug || null;
        if (routeState.poiSlug) {
          closePoi();
        }
      }
    },
    [isBigScreen, routeState.poiSlug, contentUrl, closePoi, intl, krpano]
  );

  const triggerOnChange = useMemo(
    () =>
      debounce((nVlookat: number, nHlookat: number, nFov: number) => {
        if (
          significantDifference(view.hlookat, nHlookat) ||
          significantDifference(view.vlookat, nVlookat) ||
          significantDifference(view.fov, nFov)
        ) {
          onViewChange?.({
            hlookat: hlookatConverter(nHlookat),
            vlookat: nVlookat,
            fov: nFov,
          });
        }
      }, 200),
    [view, onViewChange]
  );

  const viewChange = useCallback(() => {
    if (!krpano || krpano.get("webvr.isenabled")) return;
    const krpanoView = krpano.get("view");
    if (
      krpanoView.vlookat !== view.vlookat ||
      krpanoView.hlookat !== view.hlookat ||
      krpanoView.fov !== view.fov
    ) {
      triggerOnChange(krpanoView.vlookat, krpanoView.hlookat, krpanoView.fov);
    }
  }, [view, triggerOnChange, krpano]);

  const onLoadComplete = useCallback(() => {
    if (teasing || !krpano) return;

    hotspots.forEach((hotspot) => {
      const hs = krpano.get(`hotspot[${hotspot.slug}]`);
      if (!hs) return;
      hs.onclick = () => onPanoHotspotClick(hs);

      const text = krpano.get(`hotspot[${hotspot.slug}-text]`);
      if (text) {
        text.onclick = hs.onclick;
      }

      if (hotspot.slug === routeState.poiSlug) loadPoi(findPoi(hotspot.slug));
    });

    krpano
      .get("hotspot")
      .getArray()
      .filter((hs: any) => hs.vr_ui)
      .forEach((hs: any) => {
        hs.ath = krpano.get("view.hlookat");
        hs.visible = isInVr;
      });

    krpano
      .get("hotspot")
      .getArray()
      .filter((hs: any) => hs.name.startsWith("vr_scenario_switch-"))
      .forEach((hs: any) => {
        hs.onclick = () => {
          const scenarioSlug = hs.name.substring("vr_scenario_switch-".length);
          if (scenarioSlug) setSwitchScenario(scenarioSlug);
        };
      });

    if (isVideo) {
      const videoPlugin = krpano.get("plugin[video]")!;

      if (returnAtEndOfVideo && hasPreviousPano.current) {
        videoPlugin.onvideocomplete = () => history.goBack();
      }

      if (videoPlugin.isvideoready) {
        setLoading(false);
        videoPlugin.play();
      } else {
        videoPlugin.onvideoready = () => {
          setLoading(false);
          videoPlugin.play();
        };
      }
    } else {
      setLoading(false);
    }
  }, [
    hotspots,
    isVideo,
    returnAtEndOfVideo,
    history,
    loadPoi,
    routeState.poiSlug,
    teasing,
    isInVr,
    findPoi,
    onPanoHotspotClick,
    setSwitchScenario,
    krpano,
  ]);

  const onPanoClick = useCallback(() => {
    if (!krpano || krpano.get("webvr.isenabled") || krpano.get("is_hs_click"))
      return krpano?.set("is_hs_click", null);
    const coordinates = getCoordinates(krpano);
    const scenarioSlug = routeState.activeScenarioSlug;
    const panoClick = {
      longitude: coordinates.x,
      latitude: coordinates.y,
      panoSlug: panoNameForSurvey,
      ctrl: lastPointerEventRef.current?.ctrlKey ?? false,
      alt: lastPointerEventRef.current?.ctrlKey ?? false,
      shift: lastPointerEventRef.current?.ctrlKey ?? false,
    };
    document.dispatchEvent(new CustomEvent("panoclick", { detail: panoClick }));

    if (krpano.get("action[highlight_polygons]") !== null) krpano.call("highlight_polygons()");

    if (lastPointerEventRef.current?.ctrlKey) {
      const value = `${coordinates.x},${coordinates.y}`;
      copy(value).then(
        () => console.info(`Pano X/Y copied to clipboard: (${value})`),
        () => console.info(`Çopy failed: Clicked X/Y: ${value}`)
      );
    }

    const panoPin: PlacePanoPinOptions = {
      location: { latitude: coordinates.y, longitude: coordinates.x },
      pano: panoNameForSurvey,
      scenario: scenarioSlug,
    };
    pinPlaceFn(panoPin);
  }, [routeState.activeScenarioSlug, panoNameForSurvey, pinPlaceFn, krpano, copy]);

  const onEnterVr = useCallback(() => setIsInVr(true), [setIsInVr]);

  const onExitVr = useCallback(() => {
    if (!krpano) return;

    setIsInVr(false);

    krpano.set("view.hlookat", pano?.view.hlookat ?? 0);
    krpano.set("view.vlookat", pano?.view.vlookat ?? 0);
    krpano.call(`
      removehotspot(vr-poi);
      removehotspot(vr-poi-control-container);
      removehotspot(vr-poi-close);
      removehotspot(vr-poi-scrolldown);
      removehotspot(vr-poi-scrollup)`);
  }, [setIsInVr, pano, krpano]);

  useEffect(() => {
    if (!krpano) return;

    krpano
      .get("hotspot")
      .getArray()
      .filter((hs: any) => hs.vr_ui)
      .forEach((hs: any) => {
        hs.ath = krpano.get("view.hlookat");
        hs.visible = isInVr;
      });
  }, [isInVr, krpano]);

  const onVrControllers = useCallback(() => {
    if (!krpano) return;

    const vrControllers = krpano.get("webvr.vrcontrollers");

    vrControllers.forEach((controller) => {
      if (!controller?.hs) return;

      controller.hs.onvrcontrollerbutton = () => {
        if (controller.hs.vrbuttonstate === "down") return;

        if (!controller.hs.target?.onclick) {
          if (!controller.hs.target) krpano.call("highlight_polygons()");
          return;
        }

        if (typeof controller.hs.target.onclick === "function") {
          controller.hs.target.onclick();
        } else {
          krpano.call(controller.hs.target.onclick);
        }
      };
    });
  }, [krpano]);

  useLayoutEffect(() => {
    if (init && krpano) {
      krpano.set("events.webvr_onentervr", onEnterVr);
      krpano.set("events.webvr_onexitvr", onExitVr);
      krpano.set("events.webvr_onvrcontrollers", onVrControllers);
    }
  }, [init, krpano, onEnterVr, onExitVr, onVrControllers]);

  useLayoutEffect(() => {
    if (init && krpano) {
      krpano.set("events.gyro_onenable", () => setGyroActive(true));
      krpano.set("events.gyro_ondisable", () => setGyroActive(false));
    }
  }, [init, krpano, setGyroActive]);

  const startAutoRotate = useCallback(() => {
    if (!krpano) return;

    krpano.call("delete(events.onintrocomplete)");

    if (!panoDragged) {
      krpano?.call("autorotate.start()");
    }
  }, [krpano, panoDragged]);

  const stopAutoRotate = useCallback(() => {
    setPanoDragged("true");
    if (!krpano) return;

    krpano.call("delete(events.onautorotatestop)");
    krpano.call("autorotate.stop()");
  }, [krpano, setPanoDragged]);

  useLayoutEffect(() => {
    if (init && krpano) {
      krpano.set("events.onviewchange", movePanoIndicators);
      krpano.set("events.onviewchanged", viewChange);
      krpano.set("events.onclick", onPanoClick);
      krpano.set("events.onloadcomplete", onLoadComplete);

      if (!panoDragged && !routeState.participationItemSlug) {
        krpano.set("events.onintrocomplete", startAutoRotate);
        krpano.set("events.onautorotatestop", stopAutoRotate);
      }

      if (routeState.poiSlug) {
        loadPoi(findPoi(routeState.poiSlug));
      } else {
        if (krpano.get("webvr.isenabled")) {
          const controllerHotspots = krpano.get("webvr.vr_controller")?.split(",");
          if (controllerHotspots)
            controllerHotspots.forEach((hs: string) =>
              krpano?.set(`hotspot[${hs}].hovering`, false)
            );
        }
        removeHotspot(krpano, "text-hotspot");
        prevPoiSlug.current = null;
      }
    }

    return triggerOnChange.cancel;
  }, [
    init,
    viewChange,
    movePanoIndicators,
    onLoadComplete,
    startAutoRotate,
    stopAutoRotate,
    loadPoi,
    routeState.poiSlug,
    routeState.participationItemSlug,
    triggerOnChange.cancel,
    panoDragged,
    onPanoClick,
    findPoi,
    krpano,
  ]);

  const onXmlComplete = useCallback(() => {
    if (teasing || !krpano) return;

    const krpanoView = krpano.get("view");

    krpanoView.limitview = "range";
    krpanoView.hlookat = view.hlookat;

    if (!isInVr) {
      krpanoView.vlookat = view.vlookat;
      krpanoView.fov = view.fov;
    }

    if (pano?.view.fovType) {
      krpanoView.fovtype = pano.view.fovType;
    }
    krpanoView.fovmax = limits.fov.max;
    krpanoView.fovmin = limits.fov.min;
    krpanoView.hlookatmin = limits.hlookat.min;
    krpanoView.hlookatmax = limits.hlookat.max;
    krpanoView.vlookatmin = limits.vlookat.min;
    krpanoView.vlookatmax = limits.vlookat.max;
  }, [teasing, krpano, view, limits, isInVr]);

  useLayoutEffect(() => {
    if (init && krpano) {
      if (
        pano &&
        scenario &&
        (prevSlug.current !== pano.slug || prevScenarioSlug.current !== scenario.slug)
      ) {
        const isNewLocation = prevSlug.current !== pano.slug;
        let isBothIndoor = isNewLocation && pano.isIndoor;
        if (isBothIndoor && hasPreviousPano.current) {
          const oldLocation = state.panos.find((p) => p.slug === prevSlug.current)!;
          isBothIndoor = oldLocation.isIndoor;
        }
        const merge = !isNewLocation || isBothIndoor ? "KEEPMOVING" : "MERGE";
        let blend =
          isNewLocation && !isInVr
            ? "ZOOMBLEND(1.2, 1.2)"
            : "SLIDEBLEND(0.6, 180, 0.4, easeinoutquad)";

        if (isInVr && merge !== "KEEPMOVING") blend = "NOBLEND";

        krpano.set(`events.onxmlcomplete`, merge === "MERGE" ? onXmlComplete : null);

        if (isVideo) setLoading(true);
        krpano.call(`loadxml(${xml}, null, ${merge}, ${blend});`);
        if (prevSlug.current) hasPreviousPano.current = true;
        prevSlug.current = pano.slug;
        prevScenarioSlug.current = scenario.slug;
      } else if (panoramaView && panoramaView !== surveyState.activeView) {
        setLoading(false);
        const currentPanoType = krpano.get("image.type") as string | null;
        if (currentPanoType) {
          const currentPanoUrl = krpano.get(`image.${currentPanoType}.url`) as string;
          if (isPanoramaSource(panoramaView.dataSource)) {
            let onCustomXmlComplete = `
                  tween(view.hlookat, ${panoramaView.longitude}, 0.6, easeinoutquad);
                  tween(view.vlookat, ${panoramaView.latitude}, 0.6, easeinoutquad);`;

            if (panoramaView.fieldOfView)
              onCustomXmlComplete += `tween(view.fov, ${panoramaView.fieldOfView}, 0.6, easeinoutquad)`;

            onCustomXmlComplete += `set(events.onxmlcomplete, null);`;

            if (panoramaView.dataSource.imageUrl !== toPanoSourceImageUrl(currentPanoUrl)) {
              krpano.set(`events.onxmlcomplete`, onCustomXmlComplete.replace(/\s+/g, " "));
              krpano.call(`loadxml(${xml}, null, MERGE, ZOOMBLEND(1.2, 1.2))`);
            } else {
              krpano.call(onCustomXmlComplete);
            }
          }
          setSurveyState((c) => ({ ...c, activeView: panoramaView }));
        } else {
          krpano.call(`loadxml(${xml}, null, MERGE, ZOOMBLEND(1.2, 1.2))`);
        }
      } else {
        krpano.set("events.onxmlcomplete", null);
      }
    }
  }, [
    init,
    pano,
    scenario,
    isVideo,
    xml,
    isInVr,
    krpano,
    state.panos,
    panoramaView,
    surveyState.activeView,
    setSurveyState,
    onXmlComplete,
  ]);

  const validHotspotNames = useMemo(
    () => [
      "text-hotspot",
      ...usedHotspots.flatMap((hs) => [hs.slug, hs.slug + "-text"]),
      ...usedHotspots.filter((hs) => hs.showBadge).map((hs) => hs.slug + "-badge"),
      ...(pano?.labels.map((l) => l.id) ?? []),
      ...(routeState.participationItemSlug || routeState.question ? ["question-hotspot"] : []),
    ],
    [usedHotspots, routeState.participationItemSlug, routeState.question, pano?.labels]
  );

  useEffect(() => {
    if (!krpano) return;

    usedHotspots.forEach((hotspot) => {
      const krpanoHotspot = krpano.get(`hotspot[${hotspot.slug}]`);
      if (krpanoHotspot) {
        krpanoHotspot.linkurl = hotspot.url ? encodeXmlUrl(hotspot.url) : null;
        if (hotspot.onClick) krpanoHotspot.onclick = hotspot.onClick;
      } else {
        addHotspot(krpano, hotspot, onPanoHotspotClick);
      }
    });

    krpano
      .get("hotspot")
      .getArray()
      .filter((hotspot: any) => !validHotspotNames.includes(hotspot.name))
      .filter((hotspot: any) => !surveyState.survey || !hotspot.name.startsWith("survey-"))
      .filter((hotspot: any) => !hotspot.keep && !hotspot.vr && !hotspot.name.startsWith("vr_"))
      .forEach((hotspot: any) => krpano.call(`removehotspot(${hotspot.name})`));
  }, [
    usedHotspots,
    routeState.participationItemSlug,
    routeState.question,
    onPanoHotspotClick,
    validHotspotNames,
    krpano,
    surveyState.survey,
  ]);

  const onSurveyAnswerDragged = useCallback(
    (id: number, latitude: number, longitude: number) => {
      if (!surveyState.activePage) return;

      const activeLocationQuestions = surveyState.activePage.elements.filter(isLocationQuestion);

      const activeAnswers = activeLocationQuestions.flatMap((q) =>
        surveyState.answers.filter(findAnswer(q))
      );

      const answer = activeAnswers.find((a) => a.pins.some((p) => (p as LocalPinModel).id === id));
      const pin = answer?.pins.find((p) => (p as LocalPinModel).id === id);
      if (!pin || !answer) return;

      pin.latitude = latitude;
      pin.longitude = longitude;

      setSurveyState((current) => ({
        ...current,
        answers: [...current.answers],
      }));
    },
    [setSurveyState, surveyState]
  );

  const getPinColor = useCallback(
    (pinId: number) =>
      surveyState.activePage?.elements
        .filter(isLocationQuestion)
        .find((q) => q.pins.some((p) => p.id === pinId))
        ?.pins.find((p) => p.id === pinId)?.color ?? undefined,
    [surveyState.activePage?.elements]
  );

  useEffect(() => {
    if (!krpano || !surveyState.survey) return;

    const panoType = krpano.get("image.type") as string | null;

    if (!panoType) return;

    const panoUrl = new URL(
      krpano.get(`image.${panoType}.url`) as string,
      window.location.origin
    ).toString();

    const panoSource =
      panoramaView?.dataSource && isPanoramaSource(panoramaView.dataSource)
        ? panoramaView.dataSource
        : surveyState.survey.dataSources
            .filter(isPanoramaSource)
            .find((s) => fromPanoSourceImageUrl(s.imageUrl) === panoUrl);

    if (!panoSource) return;

    const activeLocationQuestionIds = surveyState.activePage?.elements
      .filter(isLocationQuestion)
      .map((q) => q.id);
    const activeAnswers = surveyState.answers.filter(
      (a): a is CreateLocationPickerAnswerModel =>
        activeLocationQuestionIds?.includes(a.questionId) ?? false
    );
    const pins = activeAnswers.flatMap((a) =>
      a.pins
        .filter(
          (p: PinModel): p is PanoLocationPinModel & LocalPinModel =>
            p.type === "PanoLocation" && p.hasOwnProperty("id")
        )
        .filter((p) => p.panoId === panoSource.id)
    );
    const existingHotspots = krpano
      .get("hotspot")
      .getArray()
      .filter((hotspot) => hotspot.name.startsWith("survey-"));
    pins.forEach((pin) => {
      const id = `survey-${pin.id}`;
      if (existingHotspots.some((hs) => hs.name === id)) return;
      const c = getPinColor(pin.pinDefinition);
      addSurveyHotspot(krpano, pin, onSurveyAnswerDragged, openModalFn, c);
    });
    existingHotspots
      .filter((hs) => !pins.some((p) => hs.name === `survey-${(p as LocalPinModel).id}`))
      .forEach((hs) => krpano.call(`removehotspot(${hs.name})`));
  }, [panoramaView, surveyState, krpano, openModalFn, onSurveyAnswerDragged, getPinColor]);

  useEffect(() => {
    if (participationState.panoLookat && krpano) {
      krpano.call(`
        lookto(${participationState.panoLookat.yaw}, ${participationState.panoLookat.pitch}, ${participationState.panoLookat.fov}, tween(easeoutquad, 1), true, true)`);
    }
  }, [participationState.panoLookat, krpano]);

  useEffect(() => {
    if (tourPoint && isPanoTourPoint(tourPoint) && tourPoint.data.pano === pano && krpano) {
      krpano.call(`
        lookto(${tourPoint.data.yaw}, ${tourPoint.data.pitch}, ${tourPoint.data.fov}, tween(easeoutquad, 1), true, true)`);
    }
  }, [pano, tourPoint, krpano]);

  const needInteraction = !!krpano?.get("plugin[video]")?.needuserinteraction;

  const ariaProps = useMemo<Record<`aria-${string}`, string>>(() => {
    const props: Record<`aria-${string}`, string> = {};

    if (pano?.excerpt) {
      props["aria-describedby"] = "pano-excerpt";
    }

    return props;
  }, [pano?.excerpt]);

  return (
    <PanoMedia
      className={classNames({
        "show-video-controls": isVideo && showVideoControls,
        "show-audio-controls": !!pano?.audio,
        "show-audio-timeline": pano?.audio && !pano?.repeatAudio,
        "user-question": interfaceState.question?.pickingLocation,
      })}
      onPointerUpCapture={(event: any) => (lastPointerEventRef.current = event.nativeEvent)}
      {...ariaProps}
    >
      <KrpanoViewer />
      {(!krpano || loading) && (
        <Loader>
          <LoaderIcon />
        </Loader>
      )}
      {needInteraction && (
        <PlayButton>
          <PlayIcon />
        </PlayButton>
      )}
      {krpano && children(krpano)}
    </PanoMedia>
  );
};

export default React.memo(Krpano);
